import React, { useState } from "react";
import Box from "@mui/material/Box";
import { IconButton } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Tooltip from '@mui/material/Tooltip';
import { saveAs } from 'file-saver';
import { getFileTypeConfig } from '../../utils';


const DownloadButton = ({ url, name, top = 100, inline = false, fileExtension = null }) => {
    const [isDownloading, setIsDownloading] = useState(false);

    const download = async () => {
        try {
            setIsDownloading(true);

            const fileConfig = getFileTypeConfig(fileExtension);

            // For media files (gif, mp4, mp3), we might need different handling
            const isMediaFile = ['gif', 'mp4', 'mp3'].includes(fileExtension?.toLowerCase());

            // Don't modify the URL - use it as provided
            const response = await fetch(url, {
                method: 'GET',
                mode: 'cors', // Explicitly set CORS mode
                headers: {
                    'Accept': fileConfig.accept
                    // Remove Content-Type header from request as it's for response
                },
                // For media files, ensure we get the full response
                cache: 'no-cache'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Get the blob from response
            const blob = await response.blob();

            // For media files, preserve the original blob type if it's already correct
            let finalBlob;
            if (isMediaFile && blob.type && blob.type !== 'application/octet-stream') {
                // Use the blob as-is if it already has a proper MIME type
                finalBlob = blob;
            } else {
                // Create a new blob with the correct MIME type
                finalBlob = new Blob([blob], { type: fileConfig.mimeType });
            }

            saveAs(finalBlob, name);
        } catch (error) {
            console.error('Error downloading file:', error);

            // Fallback: try opening in new window for media files
            if (['gif', 'mp4', 'mp3'].includes(fileExtension?.toLowerCase())) {
                console.log('Trying fallback download method...');
                try {
                    // Create a temporary link element
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = name;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } catch (fallbackError) {
                    console.error('Fallback download also failed:', fallbackError);
                }
            }
        } finally {
            setIsDownloading(false);
        }
    }

    // If inline is true, render without absolute positioning for table cells
    if (inline) {
        return (
            <Tooltip title={'Download'} placement="top">
                <IconButton
                    onClick={() => download()}
                    disabled={isDownloading}
                    size="small"
                    sx={{
                        color: 'primary.main',
                        '&:hover': {
                            backgroundColor: 'action.hover'
                        }
                    }}
                >
                    <FileDownloadIcon style={{ fontSize: 20 }} />
                </IconButton>
            </Tooltip>
        );
    }

    // Original absolute positioned version for backward compatibility
    const buttonStyle = {
        position: 'absolute',
        top: top,
        left: 280,
        backgroundColor: 'inherit',
        borderRadius: 0,
    };

    return (
        <Box position="absolute" top={top} width={340}>
            <Tooltip title={'Download'} placement="top">
                <IconButton
                    style={buttonStyle}
                    onClick={() => download()}
                    disabled={isDownloading}
                >
                    <FileDownloadIcon style={{ fontSize: 20 }} />
                </IconButton>
            </Tooltip>
        </Box>
    )
}


export default DownloadButton;
